import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { submitQuestionFeedback } from '../lib/feedback';
import { getUser } from '../lib/auth';

export default function QuestionFeedbackModal({ question, isOpen, onClose }) {
  const [category, setCategory] = useState('clarification');
  const [email, setEmail] = useState(() => {
    const user = getUser();
    return user?.email || '';
  });
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const categories = useMemo(() => [
    { value: 'clarification', label: 'Verduidelijking nodig' },
    { value: 'content_issue', label: 'Probleem met de vraaginhoud' },
    { value: 'answer_issue', label: 'Probleem met antwoorden/uitleg' },
    { value: 'typo', label: 'Typefout of opmaak' },
    { value: 'other', label: 'Overig' }
  ], []);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message || message.trim().length < 5) {
      toast.error('Typ een bericht van minimaal 5 tekens.');
      return;
    }
    try {
      setSubmitting(true);
      await submitQuestionFeedback(question.id, {
        category,
        email: email?.trim() || undefined,
        message: message.trim(),
        page_url: typeof window !== 'undefined' ? window.location.href : undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
      });
      toast.success('Bedankt! Je opmerking is verstuurd.');
      setMessage('');
      onClose?.();
    } catch (err) {
      const msg = err?.message || 'Het versturen van je feedback is mislukt. Probeer het later opnieuw.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg card mx-4 sm:mx-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="m-0">Vraag/opmerking</h3>
          <button className="btn-ghost" onClick={onClose} aria-label="Sluiten">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" htmlFor="qfb-category">Categorie</label>
            <select
              id="qfb-category"
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="qfb-email">Je e-mailadres (optioneel)</label>
            <input
              id="qfb-email"
              type="email"
              className="input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="qfb-message">Bericht</label>
            <textarea
              id="qfb-message"
              className="input"
              rows={5}
              placeholder="Beschrijf je vraag of opmerking"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Annuleren</button>
            <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Versturen...' : 'Versturen'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
